import { FC, useState, useCallback, useEffect } from 'react';
import { MessageCircle, Phone, Users, User, X, MapPin, Building, Calendar } from 'lucide-react';
import { useAppDispatch } from '../../hooks/redux';
import { createCustomerInteraction } from '../../store/slices/interactionsSlice';
import { Customer } from '../../lib/types';
import { getCustomerTambakId, getCustomerNoPelanggan } from '../../lib/customerUtils';
import { RichTextEditor } from './RichTextEditor';
import { useLanguage } from '../../contexts/LanguageContext';

// Helper function to display customer name with ID
const displayCustomerName = (customer: Customer): string => {
  const cleanName = customer.name?.replace(/\s*\([^)]+\)$/, '') || customer.NamaPelangganTambak || 'Unknown Customer';
  const customerId = customer.idcust || customer.id;
  return `${cleanName} (${customerId})`;
};

interface CustomerInteractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer;
  showDateTimePicker?: boolean;
  simplifiedMode?: boolean; // When true, hides interaction type, priority, and time selector
}

export const CustomerInteractionModal: FC<CustomerInteractionModalProps> = ({
  isOpen,
  onClose,
  customer,
  showDateTimePicker = true,
  simplifiedMode = false
}) => {
  const dispatch = useAppDispatch();
  const { t } = useLanguage();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to clean HTML content for storage
  const cleanHtmlContent = useCallback((html: string) => {
    // If content is empty or only contains empty tags, return empty string
    if (!html || html.trim() === '' || html.replace(/<[^>]*>/g, '').trim() === '') {
      return '';
    }
    // Keep HTML formatting for rich content storage
    return html.trim();
  }, []);
  
  // Helper function to format date to YYYY-MM-DD
  const formatDateToYYYYMMDD = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Helper function to format time to HH:mm
  const formatTimeToHHMM = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to convert YYYY-MM-DD and HH:mm to datetime format
  const convertToDateTimeString = (dateStr: string, timeStr: string): string => {
    return `${dateStr}T${timeStr}:00`;
  };

  // Detect customer type and set appropriate IDs
  const getCustomerData = useCallback(() => {
    return {
      No_pelanggan: getCustomerNoPelanggan(customer),
      // ✅ FIXED: Use safe utility function to get customer_tambak_id
      customer_tambak_id: getCustomerTambakId(customer),
      customer_name: customer.name || customer.NamaPelangganTambak || 'Unknown Customer'
    };
  }, [customer]);

  const [formData, setFormData] = useState({
    interaction_type: (simplifiedMode ? 'Visit/Meeting' : 'WhatsApp') as 'WhatsApp' | 'Phone Call' | 'Visit/Meeting',
    keterangan: '',
    priority: 'medium' as 'low' | 'medium' | 'high', // Default to medium for scheduled interactions
    interaction_time: formatTimeToHHMM(new Date()),
    interaction_date: formatDateToYYYYMMDD(new Date())
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (showDateTimePicker && !formData.interaction_date) {
      newErrors.interaction_date = t('customer_interaction.error_date_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(async () => {
    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);
      
      try {
        const customerData = getCustomerData();
        const now = new Date();
        const timeToUse = formData.interaction_time || formatTimeToHHMM(new Date());
        const interactionDateTime = convertToDateTimeString(formData.interaction_date, timeToUse);
        const interactionDate = new Date(interactionDateTime);
        
        // Determine if this is immediate scheduling (within next 5 minutes)
        const isImmediateScheduling = Math.abs(interactionDate.getTime() - now.getTime()) <= 5 * 60 * 1000;
        
        const interactionData = {
          ...customerData,
          interaction_type: formData.interaction_type,
          keterangan: cleanHtmlContent(formData.keterangan),
          priority: formData.priority,
          date_created: interactionDateTime,
          date_remind: showDateTimePicker && !isImmediateScheduling ? interactionDateTime : undefined,
          isScheduledNow: showDateTimePicker && isImmediateScheduling
        };

        const result = await dispatch(createCustomerInteraction(interactionData));
        
        if (createCustomerInteraction.fulfilled.match(result)) {
          // Provide haptic feedback
          if (window.Telegram?.WebApp?.HapticFeedback) {
            window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
          }

          // Reset form
          setFormData({
            interaction_type: simplifiedMode ? 'Visit/Meeting' : 'WhatsApp',
            keterangan: '',
            priority: 'medium',
            interaction_time: formatTimeToHHMM(new Date()),
            interaction_date: formatDateToYYYYMMDD(new Date())
          });
          setErrors({});
          
          onClose();
        } else {
          console.error('Failed to create interaction:', result.payload);
          setErrors({ submit: t('customer_interaction.error_create_failed') });
        }
      } catch (error) {
        console.error('Error creating interaction:', error);
        setErrors({ submit: t('customer_interaction.error_unexpected') });
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [formData, dispatch, onClose, isSubmitting, customer, getCustomerData, showDateTimePicker, validateForm]);

  const getInteractionTypeIcon = (type: string) => {
    switch (type) {
      case 'WhatsApp':
        return <MessageCircle className="w-5 h-5" />;
      case 'Phone Call':
        return <Phone className="w-5 h-5" />;
      case 'Visit/Meeting':
        return <Users className="w-5 h-5" />;
      default:
        return <MessageCircle className="w-5 h-5" />;
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-[70]">
      <div className="bg-white w-full max-h-[95vh] rounded-t-3xl flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b border-border shadow-sm ${
          showDateTimePicker
            ? 'bg-card'
            : 'bg-card'
        }`}>
          <div>
            <h2 className="text-xl font-bold">
              {showDateTimePicker ? t('customer_interaction.schedule_title') : t('customer_interaction.create_title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {showDateTimePicker
                ? t('customer_interaction.schedule_subtitle')
                : t('customer_interaction.create_subtitle')
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 max-h-full p-6 space-y-6">
          {/* Pre-filled Customer Details */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground">
              <User className="w-4 h-4 inline mr-2" />
              {t('customer_interaction.customer_label')}
            </label>
            <div className="p-4 bg-background rounded-lg border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                  {displayCustomerName(customer).charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-medium text-muted-foreground">{displayCustomerName(customer)}</h4>
                    {customer.status === 'active' && (
                      <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      {customer.address && (
                        <div className="flex items-center space-x-2 text-muted-foreground mb-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{customer.address}</span>
                        </div>
                      )}
                      
                      {customer.phone && (
                        <div className="flex items-center space-x-2 text-muted-foreground mb-1">
                          <Phone className="w-3 h-3" />
                          <span className="truncate">{customer.phone}</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      {customer.tambak_size && (
                        <div className="flex items-center space-x-2 text-muted-foreground mb-1">
                          <Building className="w-3 h-3" />
                          <span>{customer.tambak_size}</span>
                        </div>
                      )}
                      
                      {customer.last_visit && (
                        <div className="flex items-center space-x-2 text-muted-foreground mb-1">
                          <Calendar className="w-3 h-3" />
                          <span>Last: {new Date(customer.last_visit).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Show customer type debug info */}
                  <div className="mt-2 text-xs text-muted-foreground bg-white px-2 py-1 rounded">
                    {customer.idcust ? `Data_Customer ID: ${customer.idcust}` : 
                     customer.customer_tambak_id ? `Customer_Tambak ID: ${customer.customer_tambak_id}` : 
                     `Fallback ID: ${customer.id}`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Interaction Type - Hidden in simplified mode */}
          {!simplifiedMode && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground">
                {t('customer_interaction.type_label')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['WhatsApp', 'Phone Call', 'Visit/Meeting'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleInputChange('interaction_type', type)}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-colors ${
                      formData.interaction_type === type
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-gray-200 hover:border-input'
                    }`}
                  >
                    {getInteractionTypeIcon(type)}
                    <span className="text-xs mt-1">{t(`customer_interaction.type_${type.toLowerCase().replace(/[^a-z]/g, '_')}`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Priority - Hidden in simplified mode */}
          {!simplifiedMode && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground">
                {t('customer_interaction.priority_label')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['low', 'medium', 'high'] as const).map((priority) => (
                  <button
                    key={priority}
                    type="button"
                    onClick={() => handleInputChange('priority', priority)}
                    className={`px-4 py-2 rounded-lg border-2 transition-colors capitalize ${
                      formData.priority === priority
                        ? priority === 'high' ? 'border-red-500 bg-red-50 text-red-700' :
                          priority === 'medium' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                          'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-input'
                    }`}
                  >
                    {t(`customer_interaction.priority_${priority}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date & Time - Conditionally rendered */}
          {showDateTimePicker && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-muted-foreground">
                <Calendar className="w-4 h-4 inline mr-2" />
                {simplifiedMode ? t('customer_interaction.date_label') : t('customer_interaction.date_time_label')}
              </label>
              <div className={`grid ${simplifiedMode ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    {t('customer_interaction.date_label')}
                  </label>
                  <input
                    type="date"
                    value={formData.interaction_date}
                    onChange={(e) => handleInputChange('interaction_date', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring text-sm ${
                      errors.interaction_date ? 'border-red-300' : 'border-input'
                    }`}
                  />
                  {errors.interaction_date && (
                    <p className="text-red-600 text-xs mt-1">{errors.interaction_date}</p>
                  )}
                </div>
                
                {/* Time selector - Hidden in simplified mode */}
                {!simplifiedMode && (
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      {t('customer_interaction.time_label')}
                    </label>
                    <input
                      type="time"
                      step="60"
                      value={formData.interaction_time}
                      onChange={(e) => handleInputChange('interaction_time', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes with Rich Text Editor */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground">
              {t('customer_interaction.notes_label')}
            </label>
            <RichTextEditor
              value={formData.keterangan}
              onChange={(value) => handleInputChange('keterangan', value)}
              placeholder={t('customer_interaction.notes_placeholder')}
              minHeight="120px"
              className="focus-within:ring-2 focus-within:ring-ring"
            />
            <div className="text-xs text-muted-foreground bg-primary/10 p-2 rounded">
              <strong>{t('customer_interaction.rich_editor_label')}:</strong>
              <span className="ml-2">{t('customer_interaction.rich_editor_hint')}</span>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-input text-muted-foreground rounded-lg font-medium hover:bg-background transition-colors"
            >
              {t('customer_interaction.cancel_button')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex-1 px-6 py-3 text-primary-foreground rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                showDateTimePicker
                  ? 'bg-primary hover:bg-primary/90'
                  : 'bg-primary hover:bg-primary/90'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                  {showDateTimePicker ? t('customer_interaction.scheduling') : t('customer_interaction.creating')}
                </>
              ) : (
                showDateTimePicker ? t('customer_interaction.schedule_button') : t('customer_interaction.create_button')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};